import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';

// Module-level flag — prevents duplicate Capacitor listener registration
// when this hook is called from multiple components simultaneously.
let nativeInitialized = false;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw     = atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

const SW_URL = '/sw.js';

export function usePushNotifications() {
  const [isNative,     setIsNative]     = useState(false);
  const [isSupported,  setIsSupported]  = useState(false);
  const [permission,   setPermission]   = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading,    setIsLoading]    = useState(false);
  const [error,        setError]        = useState(null);
  const [registration, setRegistration] = useState(null);

  // ── Bootstrap ─────────────────────────────────────────────
  useEffect(() => {
    let Capacitor, PushNotifications;
    try {
      Capacitor = require('@capacitor/core').Capacitor;
    } catch {}

    if (Capacitor && Capacitor.isNativePlatform()) {
      try {
        PushNotifications = require('@capacitor/push-notifications').PushNotifications;
      } catch {}
      setIsNative(true);
      setIsSupported(true);
      initNative(Capacitor, PushNotifications);
      return;
    }

    // Web path
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setIsSupported(supported);
    if (!supported) return;

    setPermission(Notification.permission);
    initWeb();
  }, []); // eslint-disable-line

  // ── Native init ───────────────────────────────────────────
  async function initNative(Capacitor, PushNotifications) {
    if (!PushNotifications) return;
    if (nativeInitialized) {
      // Another hook instance already registered listeners.
      // Sync permission state only.
      try {
        const { receive } = await PushNotifications.checkPermissions();
        if (receive === 'granted') { setPermission('granted'); setIsSubscribed(true); }
      } catch {}
      return;
    }
    nativeInitialized = true;

    try {
      const { receive } = await PushNotifications.checkPermissions();
      if (receive === 'granted') { setPermission('granted'); setIsSubscribed(true); }

      PushNotifications.addListener('registration', async ({ value: token }) => {
        try {
          const platform = Capacitor.getPlatform(); // 'ios' | 'android'
          await api.post('/api/push/register-device', { token, platform });
          setIsSubscribed(true);
          setPermission('granted');
        } catch (err) {
          console.warn('Native push registration failed:', err.message);
        }
      });

      PushNotifications.addListener('registrationError', (err) => {
        console.error('Native push registration error:', err);
        setError('Push registration failed. Please try again.');
      });

      // Foreground notification — dispatch for any in-app banner listening
      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        window.dispatchEvent(new CustomEvent('ignite-push-received', { detail: notification }));
      });

      // Notification tap — navigate to the deep-link url stored in notification.data
      PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        const url = action.notification?.data?.url;
        if (url) window.dispatchEvent(new CustomEvent('ignite-navigate', { detail: { url } }));
      });

      // Auto-request permission and register on first run
      if (receive !== 'granted') {
        const result = await PushNotifications.requestPermissions();
        if (result.receive === 'granted') {
          await PushNotifications.register();
        } else {
          setPermission('denied');
        }
      } else {
        await PushNotifications.register();
      }
    } catch (err) {
      console.warn('Native push init error:', err.message);
    }
  }

  // ── Web init ──────────────────────────────────────────────
  async function initWeb() {
    try {
      const reg = await navigator.serviceWorker.register(SW_URL, { scope: '/' });

      await new Promise((resolve) => {
        if (reg.active) return resolve();
        const sw = reg.installing || reg.waiting;
        if (sw) {
          sw.addEventListener('statechange', function handler(e) {
            if (e.target.state === 'activated') { sw.removeEventListener('statechange', handler); resolve(); }
          });
        } else {
          navigator.serviceWorker.addEventListener('controllerchange', () => resolve(), { once: true });
        }
      });

      setRegistration(reg);

      const existingSub = await reg.pushManager.getSubscription();

      if (existingSub) {
        const subJson = existingSub.toJSON();
        try {
          await api.post('/api/push/subscribe', {
            endpoint:  subJson.endpoint,
            keys:      subJson.keys,
            userAgent: navigator.userAgent.slice(0, 200),
          });
        } catch (syncErr) {
          console.warn('Push sync to backend failed:', syncErr.message);
        }
        setIsSubscribed(true);
      } else if (Notification.permission === 'granted') {
        try {
          await subscribeWebInternal(reg);
        } catch (resubErr) {
          console.warn('Push re-subscribe failed:', resubErr.message);
        }
      }
    } catch (err) {
      console.warn('Push init error:', err.message);
    }
  }

  async function subscribeWebInternal(reg) {
    const { key } = await api.get('/api/push/vapid-public-key');
    const applicationServerKey = urlBase64ToUint8Array(key);

    const subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });

    const subJson = subscription.toJSON();

    await api.post('/api/push/subscribe', {
      endpoint:  subJson.endpoint,
      keys:      subJson.keys,
      userAgent: navigator.userAgent.slice(0, 200),
    });

    setIsSubscribed(true);
    return subscription;
  }

  // ── Public: subscribe ─────────────────────────────────────
  const subscribe = useCallback(async () => {
    if (isNative) {
      let PushNotifications;
      try { PushNotifications = require('@capacitor/push-notifications').PushNotifications; } catch {}
      if (!PushNotifications) return false;

      setIsLoading(true);
      setError(null);
      try {
        const result = await PushNotifications.requestPermissions();
        if (result.receive === 'granted') {
          await PushNotifications.register();
          setIsLoading(false);
          return true;
        } else {
          setError('Notifications blocked. Enable them in device Settings.');
          setPermission('denied');
          setIsLoading(false);
          return false;
        }
      } catch (err) {
        setError(err.message || 'Failed to enable push notifications.');
        setIsLoading(false);
        return false;
      }
    }

    // Web path
    if (!isSupported) {
      setError('Push notifications are not supported in this browser.');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      let reg = registration;
      if (!reg) {
        reg = await navigator.serviceWorker.register(SW_URL, { scope: '/' });
        setRegistration(reg);
      }

      if (Notification.permission !== 'granted') {
        const perm = await Notification.requestPermission();
        setPermission(perm);
        if (perm !== 'granted') {
          setError(perm === 'denied'
            ? 'Notifications blocked. Enable them in your device settings.'
            : 'Permission not granted.');
          setIsLoading(false);
          return false;
        }
      }

      await subscribeWebInternal(reg);
      setIsLoading(false);
      return true;
    } catch (err) {
      console.error('Push subscribe error:', err);
      setError(err.message || 'Failed to enable push notifications.');
      setIsLoading(false);
      return false;
    }
  }, [isNative, isSupported, registration]); // eslint-disable-line

  // ── Public: unsubscribe ───────────────────────────────────
  const unsubscribe = useCallback(async () => {
    if (isNative) return; // FCM tokens are managed server-side; no client unsubscribe

    if (!registration) return;
    setIsLoading(true);
    setError(null);

    try {
      const sub = await registration.pushManager.getSubscription();
      if (sub) {
        await api.post('/api/push/unsubscribe', { endpoint: sub.endpoint }).catch(() => {});
        await sub.unsubscribe();
      }
      setIsSubscribed(false);
    } catch (err) {
      setError(err.message || 'Failed to disable push notifications.');
    } finally {
      setIsLoading(false);
    }
  }, [isNative, registration]); // eslint-disable-line

  return { isNative, isSupported, permission, isSubscribed, isLoading, subscribe, unsubscribe, error };
}
